import { useEffect, useState } from "react";
import {
  Box, Button, Grid, TextField, Stack,
  IconButton, Divider, InputAdornment,
  FormControlLabel
} from "@mui/material";
import ThemeSwitch from "components/form/ThemeSwitch";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import api, { resolveAssetUrl } from "../../utils/api";
import {
  EntityFormDialog,
  FormDialogHeader,
  FormDialogContent,
  FormDialogFooter,
  FormSection,
  FormEmptyState,
  FormImageUpload
} from "components/form/EntityFormDialog";
import { brand } from "utils/designTokens";
import useFormValidation from "hooks/useFormValidation";
import { useCurrency } from "contexts/CurrencyContext";
import { validators } from "utils/formValidation";

const emptyVariant = () => ({ name: "", price: "", description: "" });

export default function ProductFilteredByStoreForm({ open, productId, storeId, storeName, initialCategoryId, onSave, onClose }) {
  const isEdit = Boolean(productId);

  const [form, setForm] = useState({
    name: "", name_ar: "", price: "",
    description: "", is_veg: false,
    highlight: false, sequence: 0, image: null,
  });

  const [variants, setVariants] = useState([]);
  const [preview, setPreview] = useState(null);
  const [categoryId, setCategoryId] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { currency } = useCurrency();
  const { formError, validate, setServerError, clearFieldError, fieldErrorProps, setFieldErrors } = useFormValidation();

  useEffect(() => {
    if (open && isEdit) {
      fetchProduct();
    } else if (open) {
      setForm({
        name: "", name_ar: "", price: "",
        description: "", is_veg: false,
        highlight: false, sequence: 0, image: null,
      });
      setVariants([]);
      setPreview(null);
      setCategoryId(initialCategoryId || "");
    }
  }, [open, productId, isEdit, initialCategoryId]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/product/${productId}`);
      const data = res.data.data;

      setForm({
        name: data.name || "",
        name_ar: data.name_ar || "",
        price: data.price ?? "",
        description: data.description || "",
        is_veg: data.is_veg ?? false,
        highlight: data.highlight ?? false,
        sequence: data.sequence ?? 0,
        image: null,
      });

      const imgUrl = data.image_url || data.image || data.Doc?.imageUrl || data.Doc?.attachment_url || data.doc?.imageUrl || data.doc?.attachment_url;
      if (imgUrl) setPreview(resolveAssetUrl(imgUrl));

      setCategoryId(data.category_id || initialCategoryId || "");

      if (data.ProductVariants?.length) {
        setVariants(
          data.ProductVariants.map((v) => ({
            name: v.name || "",
            price: v.price ?? "",
            description: v.description || "",
          }))
        );
      } else {
        setVariants([]);
      }
    } catch (err) {
      console.error("fetchProduct error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name) clearFieldError(name);
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((p) => ({ ...p, image: file }));
    setPreview(URL.createObjectURL(file));
    e.target.value = "";
  };

  const addVariant = () => setVariants((p) => [...p, emptyVariant()]);
  const removeVariant = (i) => setVariants((p) => p.filter((_, idx) => idx !== i));
  const handleVariantChange = (i, field, value) => {
    if (field === "name") clearFieldError(`variant_${i}_name`);
    setVariants((p) => p.map((v, idx) => (idx === i ? { ...v, [field]: value } : v)));
  };

  const validateVariantFields = () => {
    const errors = {};
    variants.forEach((v, i) => {
      if (!String(v.name ?? "").trim()) {
        errors[`variant_${i}_name`] = "Variant name is required";
      }
    });
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setServerError("Each variant must have a name.", null, { toast: false });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    const ok = validate(
      {
        name: [validators.required("Product name")],
        price: [validators.required("Base price"), validators.positiveNumber("Base price")]
      },
      form
    );

    if (!ok) return;

    if (variants.length > 0 && !validateVariantFields()) return;

    if (!storeId) {
      setServerError("Store selection is required. Choose a store from the list first.");
      return;
    }
    const cid = categoryId || initialCategoryId;
    if (!cid) {
      setServerError("Category context is required.");
      return;
    }
    if (variants.length > 0 && Number(variants[0].price) !== Number(form.price)) {
      setServerError(`First variant price must match base price (${form.price}).`);
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("name_ar", form.name_ar);
      fd.append("store_id", storeId);
      fd.append("category_id", cid);
      fd.append("price", form.price);
      fd.append("description", form.description);
      fd.append("is_veg", form.is_veg);
      fd.append("highlight", form.highlight);
      fd.append("sequence", form.sequence);
      if (form.image instanceof File) {
        fd.append("image", form.image, form.image.name);
      }
      if (variants.length > 0) fd.append("variant_list", JSON.stringify(variants));

      if (isEdit) {
        await api.put(`/product/edit/${productId}`, fd);
      } else {
        await api.post("/product/add", fd);
      }
      onSave();
      onClose();
    } catch (err) {
      setServerError(err.response?.data?.message || "Failed to save product");
    } finally {
      setSubmitting(false);
    }
  };

  const addSlotButton = (
    <Button
      size="small"
      startIcon={<PlusOutlined />}
      onClick={addVariant}
      variant="text"
      sx={{ color: brand.primary, fontWeight: 500, textTransform: "none", minWidth: 0 }}
    >
      Add variant
    </Button>
  );

  return (
    <EntityFormDialog open={open} onClose={onClose} maxWidth="md">
      <FormDialogHeader
        icon={<Inventory2RoundedIcon />}
        title={isEdit ? "Edit Product" : "New Product"}
        storeName={storeName}
        onClose={onClose}
      />

      <FormDialogContent loading={loading} formError={formError}>
        <Stack spacing={2.5}>
          <FormSection title="Product details">
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required size="small" label="Name (EN)" name="name" value={form.name} onChange={handleChange} {...fieldErrorProps("name")} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Name (AR)" name="name_ar" value={form.name_ar} onChange={handleChange} inputProps={{ dir: "rtl" }} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  type="number"
                  label="Base price"
                  name="price"
                  value={form.price}
                  onChange={handleChange}
                  InputProps={{ startAdornment: <InputAdornment position="start">{currency.symbol}</InputAdornment> }}
                  {...fieldErrorProps("price")}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField fullWidth size="small" type="number" label="Sequence" name="sequence" value={form.sequence} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Stack direction="row" spacing={2} sx={{ pt: { sm: 0.5 } }}>
                  <FormControlLabel
                    control={<ThemeSwitch size="small" checked={form.is_veg} onChange={handleChange} name="is_veg" />}
                    label="Veg"
                  />
                  <FormControlLabel
                    control={<ThemeSwitch size="small" checked={form.highlight} onChange={handleChange} name="highlight" />}
                    label="Highlight"
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" multiline rows={2} label="Description" name="description" value={form.description} onChange={handleChange} />
              </Grid>
            </Grid>

            <FormImageUpload
              label="Image"
              preview={preview}
              placeholderIcon={<ImageOutlinedIcon />}
              hasFile={Boolean(form.image)}
              onFileChange={handleFile}
              hint="Optional. JPG, PNG, or WEBP."
            />
          </FormSection>

          <FormSection title="Portions & variants" action={addSlotButton}>
            {variants.length === 0 ? (
              <FormEmptyState>No variations yet.</FormEmptyState>
            ) : (
              <Stack spacing={1.5} divider={<Divider />}>
                {variants.map((v, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                    <Grid container spacing={1.5} sx={{ flex: 1 }}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          required
                          size="small"
                          label="Variant name"
                          value={v.name}
                          onChange={(e) => handleVariantChange(i, "name", e.target.value)}
                          {...fieldErrorProps(`variant_${i}_name`)}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          fullWidth
                          required
                          size="small"
                          type="number"
                          label="Price"
                          value={v.price}
                          onChange={(e) => handleVariantChange(i, "price", e.target.value)}
                          InputProps={{ startAdornment: <InputAdornment position="start">{currency.symbol}</InputAdornment> }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={5}>
                        <TextField fullWidth size="small" label="Note" value={v.description} onChange={(e) => handleVariantChange(i, "description", e.target.value)} />
                      </Grid>
                    </Grid>
                    <IconButton size="small" color="error" onClick={() => removeVariant(i)} sx={{ mt: 0.25 }}>
                      <DeleteOutlined />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            )}
          </FormSection>
        </Stack>
      </FormDialogContent>

      <FormDialogFooter
        onCancel={onClose}
        onSubmit={handleSubmit}
        submitting={submitting}
        submitLabel={isEdit ? "Save changes" : "Create product"}
      />
    </EntityFormDialog>
  );
}
